import React, { useState, useEffect, useCallback } from 'react'
import { Typography, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
} from '@mui/material'
import { useAtom } from 'jotai'
import { selectedVoicesAtom } from 'atoms'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import SubSettingsArea from 'components/SubSettingsArea'

const Settings = () => {
  const { t, i18n } = useTranslation()
  const [voices, setVoices] = useState([])
  const [selectedVoices, setSelectedVoices] = useAtom(selectedVoicesAtom)
  const [selectedVoice, setSelectedVoice] = useState(null)
  const currentLanguage = i18n.language

  // Update the stored voices for the current language
  const updateSelectedVoice = useCallback(
    (voice) => {
      setSelectedVoice(voice)
      setSelectedVoices((prev) => ({
        ...prev,
        [currentLanguage]: voice.voiceURI,
      }))
    },
    [currentLanguage, setSelectedVoices]
  )

  useEffect(() => {
    const loadVoices = () => {
      const voicesList = speechSynthesis.getVoices()
      const filteredVoices = voicesList.filter((voice) =>
        voice.lang.startsWith(currentLanguage)
      )
      setVoices(filteredVoices)

      const storedVoiceURI = selectedVoices[currentLanguage]
      const storedVoice = filteredVoices.find(
        (voice) => voice.voiceURI === storedVoiceURI
      )

      if (storedVoice) {
        setSelectedVoice(storedVoice) // Set the stored voice if found
      } else if (filteredVoices.length > 0) {
        updateSelectedVoice(filteredVoices[0]) // Default to the first voice
      } else {
        setSelectedVoice(null) // No voices available
      }
    }

    loadVoices()

    if (speechSynthesis.onvoiceschanged !== undefined) {
      speechSynthesis.onvoiceschanged = loadVoices
    }
  }, [currentLanguage, selectedVoices, updateSelectedVoice])

  const readSentence = () => {
    const readDemo = t('readDemo')
    const utterance = new SpeechSynthesisUtterance(readDemo)

    if (selectedVoice) {
      utterance.voice = selectedVoice
      utterance.lang = selectedVoice.lang
    }

    speechSynthesis.speak(utterance)
  }

  return (
    <>
      <FormControl style={{ marginBottom: '20px' }}>
        <InputLabel id="voice-select-label">t{'selectVoice'}</InputLabel>
        <Select
          labelId="voice-select-label"
          label="Select Voice"
          // Ensure the value is either a valid voice or an empty string if no voice is available
          value={selectedVoice ? selectedVoice.name : ''}
          onChange={(e) => {
            const selectedVoiceName = e.target.value
            const selected = voices.find(
              (voice) => voice.name === selectedVoiceName
            )
            if (selected) {
              updateSelectedVoice(selected) // Update both the local and stored voice
            }
          }}
          displayEmpty
        >
          {voices.length === 0 ? (
            <MenuItem value="">{t('NoVoicesAvailable')}</MenuItem>
          ) : (
            voices.map((voice) => (
              <MenuItem key={voice.name} value={voice.name}>
                {`${voice.name} (${voice.lang})`}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
      <SubSettingsArea text={t('testVoice')}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            color="primary"
            aria-label="play"
            onClick={readSentence}
            size="large"
          >
            <PlayCircleOutlineIcon fontSize="inherit" />
          </IconButton>
          <Typography
            variant="body1"
            sx={{ fontStyle: 'italic', marginLeft: 1 }}
          >
            {t('readDemo')}
          </Typography>
        </Box>
      </SubSettingsArea>
    </>
  )
}

export default Settings
